import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import Print from "../../../../public/assets/images/printer-outline.svg";
import exportIcon from "../../../../public/assets/images/download-outline.svg";
import { usePrint } from '../../../hooks/usePrint'
import { useProfileDisplay } from '../../../hooks/useProfileDisplay'
import Dropdown, { DropdownOption } from '../../../components/common/Dropdown'
import Table from '../../../components/common/Table/Table'
import { usePaginationTracking } from '../../../hooks/usePaginationTracking'
import { transformRequestsToTableRows, DEFAULT_REQUEST_COLUMNS } from '../../../utils/tabDataTransform.tsx'
import { getAllRequests, setHistorySortOrder } from '../../../store/slices/Requestor/employeeInfoSlice'
import { exportRequestorRequests } from '../../../store/slices/Requestor/requestorExportSlice'
import Loader from '../../../components/common/Loader';
import { useAppDispatch } from '../../../store/hooks'
import api from '../../../api/axios'
import endpoints from '../../../api/endpoints'


const timeOptions: DropdownOption[] = [
  { id: '1', label: 'Last 30 days', value: '1' },
  { id: '2', label: 'Last 60 days', value: '2' },
  { id: '3', label: 'Last 90 days', value: '3' },
  { id: '4', label: 'Year to Date', value: '4' },
  { id: '5', label: 'Previous Year', value: '5' },
];

type RequestSortColumn = 'fromDate' | 'hoursRequested'

const TimeOffRequestsHistory: React.FC = () => {
  const [selectedTimeOption, setSelectedTimeOption] = React.useState<DropdownOption>(timeOptions[2])
  const { printRef, handlePrint } = usePrint({ fontSize: '9px' })
  const { userInfo, userNuid, primaryApproverInfo } = useProfileDisplay()
  const { mode } = useParams<{ mode?: string }>()
  const isAdminMode = mode === 'admin'
  const manualEmployee = useSelector((state: any) => state.manualTimeOff.selectedEmployee)
  const employeeInfoData = useSelector((state: any) => state.employeeInfo.employeeInfo.data)
  const printDisplayName = isAdminMode
    ? `${manualEmployee?.name || 'Employee'} (${manualEmployee?.nuid || 'Unknown'})`
    : `${userInfo}${userNuid}`
  const printDisplayApprover = isAdminMode
    ? (employeeInfoData?.primaryApprover || 'NA')
    : primaryApproverInfo
  const dispatch = useAppDispatch();
  const { requests, totalRecords, pageSize, currentPage, loading, sortOrder } = useSelector((state: any) => state.employeeInfo.timeOffHistory);
  const exportLoading = useSelector((state: any) => state.requestorExport.loading)
  const [activeSortColumn, setActiveSortColumn] = React.useState<string>('dateRequestedSortValue')
  
  // Print state management
  const [printRows, setPrintRows] = React.useState<any[]>([])
  const [isPrinting, setIsPrinting] = React.useState(false)
  // Calculate Year to Date (current month) and Previous Year (current month + 12 months)
  const getTimePeriodValue = (option: DropdownOption) => {
    const currentMonth = new Date().getMonth() + 1; // 1-12
    if (option.value === '4') {
      return currentMonth; // Year to Date = current month
    } else if (option.value === '5') {
      return currentMonth + 12; // Previous Year = current month + 12 months
    }
    return Number(option.value);
  };

  // Track filter and pagination changes
  const { filterChanged, pageChanged, pageSizeChanged, updateRefs } = usePaginationTracking(
    currentPage,
    pageSize,
    selectedTimeOption.value
  );

  const getApiSortColumn = React.useCallback(
    (columnKey: string): RequestSortColumn =>
      columnKey === 'hoursRequested' ? 'hoursRequested' : 'fromDate',
    []
  )

  const currentApiSortColumn = React.useMemo(
    () => getApiSortColumn(activeSortColumn),
    [activeSortColumn, getApiSortColumn]
  )

  // Transform API data to table rows
  const rows = transformRequestsToTableRows(requests);
  
  // Handle sorting by calling API with new sortOrder and sortColumn
  const handleSort = (columnKey: string, direction: 'ASC' | 'DESC') => {
    setActiveSortColumn(columnKey)
    dispatch(setHistorySortOrder(direction))
    const apiSortColumn = getApiSortColumn(columnKey)
    if (selectedTimeOption?.value) {
      dispatch(getAllRequests({
        type: 'HISTORY',
        page: 1,
        perPage: pageSize,
        timePeriod: getTimePeriodValue(selectedTimeOption),
        sortOrder: direction,
        sortColumn: apiSortColumn,
      }));
    }
  };

  // Reset page to 1 when time period changes
  useEffect(() => {
    if (filterChanged) {
      dispatch({ type: 'employeeInfo/setHistoryPage', payload: 1 });
    }
  }, [selectedTimeOption, dispatch, filterChanged]);

  // Fetch when pagination or time period changes
  useEffect(() => {
    // On initial mount, skip - parent hook handles initial load
    if (!filterChanged && !pageChanged && !pageSizeChanged) {
      return
    }

    updateRefs()

    if (selectedTimeOption?.value) {
      dispatch(getAllRequests({
        type: 'HISTORY',
        page: currentPage,
        perPage: pageSize,
        timePeriod: getTimePeriodValue(selectedTimeOption),
        sortOrder,
        sortColumn: currentApiSortColumn,
      }));
    }
  }, [dispatch, selectedTimeOption, pageSize, currentPage, filterChanged, pageChanged, pageSizeChanged, updateRefs, sortOrder, currentApiSortColumn]);

  const handlePageChange = (page: number) => {
    dispatch({ type: 'employeeInfo/setHistoryPage', payload: page });
  };

const handleExport = () => {
  const now = new Date()

  dispatch(
    exportRequestorRequests({
      date: now.toISOString().split('T')[0],
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timePeriod: getTimePeriodValue(selectedTimeOption),
      type: 'HISTORY',
      sortOrder,
      sortColumn: currentApiSortColumn,
    })
  )
}

/**
 * Fetch all records for printing without mutating Redux list pagination state.
 * Uses totalRecords directly for perPage, matching approver pattern.
 */
const handlePrintWithApi = async () => {
  try {
    setIsPrinting(true)

    const now = new Date()
    const response = await api.get(
      endpoints.requester.getAllTimeOffReqList({
        type: 'HISTORY',
        year: now.getFullYear(),
        month: now.getMonth() + 1,
        day: now.getDate(),
        page: 1,
        perPage: totalRecords,
        timePeriod: getTimePeriodValue(selectedTimeOption),
        sortOrder,
        sortColumn: currentApiSortColumn,
      })
    )

    const items = response.data?.items
    setPrintRows(Array.isArray(items) ? items : [])

    // Wait for DOM update before opening print dialog
    setTimeout(() => {
      handlePrint()
      setIsPrinting(false)
    }, 0)
  } catch {
    setIsPrinting(false)
  }
}


  return (
    <div>
      <style>
        {`
#temp-print-container {
  display: none;
}

@media print {
  @page {
    size: landscape;
    margin: 10mm;
  }

  /* Show print table */
  #temp-print-container {
    display: block !important;
    width: 100% !important;
    max-width: 100% !important;
    overflow: visible !important;
  }

  #temp-print-container table {
    width: 100% !important;
    border-collapse: collapse !important;
  }

  #temp-print-container th,
  #temp-print-container td {
    border: 1px solid #ddd !important;
    padding: 8px !important;
    text-align: left !important;
  }

  #temp-print-container th {
    background-color: #f5f5f5 !important;
    font-weight: bold !important;
  }
}
        `}
      </style>
         {(exportLoading || isPrinting) && <Loader />}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between w-full gap-3">
        <div className="w-full md:w-auto">
          <Dropdown
            label={'Time Period'}
            options={timeOptions}
            selectedValue={selectedTimeOption}
            onSelect={(opt) => setSelectedTimeOption(opt)}
            buttonClassName="p2 -medium w-full md:w-auto"
            buttonLabel={selectedTimeOption.label}
            menuClassName=""
          />
        </div>
        <div className="w-full md:w-auto">
          <div className="flex  items-baseline justify-end gap-2">
            <button type="button"
              className="button -secondary flex items-center w-full md:w-auto justify-center md:justify-start"
              onClick={handlePrintWithApi}
              disabled={isPrinting}
            >
              <img src={Print} alt="Print Icon" className="inline-block mr-2" />
              <span>Print</span>
            </button>
            <button type="button"
              className="button -secondary flex items-center w-full md:w-auto"
              onClick={handleExport}
              title="Export File"
            >
              <img
                src={exportIcon}
                alt="Export Icon"
                className="inline-block mr-2"
              />
              <span className="text-sm leading-5">Export File</span>
            </button>
          </div>
        </div>
      </div>

      <div className="mt-6">
        {loading && <Loader />}
        {!loading && rows.length === 0 && (
          <div className="flex items-center justify-center min-h-96 py-12">
            <div className="text-center">
              <p className="p3 -medium">There are no Time Off Requests.</p>
            </div>
          </div>
        )}
        {!loading && rows.length > 0 && (
          <Table
            columns={DEFAULT_REQUEST_COLUMNS}
            rows={rows as any[]}
            showPagination={true}
            paginationProps={{
              totalItems: totalRecords,
              itemsPerPage: pageSize,
            }}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onSort={handleSort}
            sortColumn={activeSortColumn}
            sortDirection={sortOrder}
          />
        )}
      </div>
      {printRows.length > 0 && (
        <div id="temp-print-container" ref={printRef}>
          <div style={{marginBottom: '16px', paddingBottom: '8px', borderBottom: '1px solid #ccc'}}>
            <p style={{margin: '0 0 4px 0', fontWeight: 'bold', fontSize: '14px'}}>{printDisplayName}</p>
            <p style={{margin: '0 0 4px 0', fontSize: '12px'}}>Primary Approver: {printDisplayApprover}</p>
            <p style={{margin: '0 0 4px 0', fontSize: '12px'}}>History</p>
            <p style={{margin: '0', fontSize: '12px'}}>Time Period: {selectedTimeOption.label}</p>
          </div>
          <Table
            columns={DEFAULT_REQUEST_COLUMNS}
            rows={transformRequestsToTableRows(printRows)}
            isSorting={false}
            showPagination={false}
          />
        </div>
      )}
    </div>
  )
}

export default TimeOffRequestsHistory